import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Users,
  MapPin,
  Phone,
  Mail,
  Crown,
  Loader2,
  Search,
  ChevronRight,
  UserCog,
  GitBranch,
  Shield,
} from "lucide-react";

const BranchManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("branches");

  // Dialogs
  const [showNewBranch, setShowNewBranch] = useState(false);
  const [showEditBranch, setShowEditBranch] = useState(false);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [newBranch, setNewBranch] = useState({
    name: "",
    code: "",
    address: "",
    city: "",
    state: "",
    country: "Zambia",
    phone: "",
    email: "",
    is_headquarters: false,
  });

  useEffect(() => {
    fetchBranches();
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches/all`, { withCredentials: true });
      setBranches(response.data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      // Try regular endpoint if admin endpoint fails
      try {
        const response = await axios.get(`${API}/branches`, { withCredentials: true });
        setBranches(response.data);
      } catch (e) {
        console.error("Failed to fetch branches:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`, { withCredentials: true });
      setRoles(response.data);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const handleCreateBranch = async () => {
    if (!newBranch.name.trim() || !newBranch.code.trim() || !newBranch.city.trim()) {
      toast.error("Branch name, code, and city are required");
      return;
    }

    try {
      await axios.post(`${API}/branches`, newBranch, { withCredentials: true });
      toast.success("Branch created successfully!");
      setShowNewBranch(false);
      setNewBranch({
        name: "",
        code: "",
        address: "",
        city: "",
        state: "",
        country: "Zambia",
        phone: "",
        email: "",
        is_headquarters: false,
      });
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create branch");
    }
  };

  const handleUpdateBranch = async () => {
    if (!selectedBranch) return;

    try {
      await axios.put(`${API}/branches/${selectedBranch.branch_id}`, selectedBranch, { withCredentials: true });
      toast.success("Branch updated successfully!");
      setShowEditBranch(false);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update branch");
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm("Delete this branch? All users will be unassigned.")) return;

    try {
      await axios.delete(`${API}/branches/${branchId}`, { withCredentials: true });
      toast.success("Branch deleted");
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete branch");
    }
  };

  const handleAssignUserToBranch = async (userId, branchId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/branch`, { branch_id: branchId }, { withCredentials: true });
      toast.success("User assigned to branch");
      fetchUsers();
      setShowAssignUser(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign user");
    }
  };

  const handleAssignSupervisor = async (userId, supervisorId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/supervisor`, { supervisor_id: supervisorId }, { withCredentials: true });
      toast.success("Supervisor assigned");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign supervisor");
    }
  };

  const getRoleDisplayName = (role) => {
    const roleInfo = roles.find(r => r.role === role);
    return roleInfo?.display_name || role?.replace("_", " ");
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      ceo: "bg-purple-100 text-purple-700",
      admin: "bg-red-100 text-red-700",
      accounts: "bg-green-100 text-green-700",
      data_entry: "bg-gray-100 text-gray-700",
      project_manager: "bg-blue-100 text-blue-700",
      constructor: "bg-orange-100 text-orange-700",
      letting_manager: "bg-teal-100 text-teal-700",
      sales_manager: "bg-indigo-100 text-indigo-700",
      agent: "bg-yellow-100 text-yellow-700",
      support: "bg-pink-100 text-pink-700",
      people_culture: "bg-cyan-100 text-cyan-700",
    };
    return colors[role] || "bg-gray-100 text-gray-600";
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.branch_id === branchId);
    return branch?.name || "Unassigned";
  };

  const getSupervisorName = (userId) => {
    const supervisor = users.find(u => u.user_id === userId);
    return supervisor?.name || "None";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Organization Management</h1>
          <p className="text-gray-500">Manage branches, roles, and reporting structure</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          {["branches", "users", "hierarchy"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? "border-primary text-primary font-medium"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "hierarchy" ? "Role Hierarchy" : tab}
            </button>
          ))}
        </div>
      </div>

      {/* Branches Tab */}
      {activeTab === "branches" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Branches ({branches.length})</h2>
            <Button onClick={() => setShowNewBranch(true)} data-testid="add-branch-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Branch
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((branch) => (
              <div
                key={branch.branch_id}
                className="bg-white rounded-lg border p-5 hover:shadow-md transition-shadow"
                data-testid={`branch-card-${branch.branch_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{branch.name}</h3>
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{branch.code}</span>
                    </div>
                  </div>
                  {branch.is_headquarters && (
                    <Crown className="w-5 h-5 text-yellow-500" title="Headquarters" />
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{branch.city}, {branch.country}</span>
                  </div>
                  {branch.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>{branch.phone}</span>
                    </div>
                  )}
                  {branch.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span>{branch.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{users.filter(u => u.branch_id === branch.branch_id).length} users</span>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setSelectedBranch(branch);
                      setShowEditBranch(true);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  {!branch.is_headquarters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteBranch(branch.branch_id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {branches.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No branches created yet.</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowNewBranch(true)}>
                  Create your first branch
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <span className="text-sm text-gray-500">{filteredUsers.length} users</span>
          </div>

          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">User</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Branch</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Reports To</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.map((u) => (
                  <tr key={u.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-medium">
                            {u.name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                        {getRoleDisplayName(u.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${u.branch_id ? "text-gray-700" : "text-gray-400"}`}>
                        {getBranchName(u.branch_id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${u.reports_to ? "text-gray-700" : "text-gray-400"}`}>
                        {getSupervisorName(u.reports_to)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setShowAssignUser(true);
                          }}
                          title="Assign Branch & Supervisor"
                        >
                          <UserCog className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role Hierarchy Tab */}
      {activeTab === "hierarchy" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5" /> Organizational Hierarchy
            </h2>
            
            <div className="space-y-1">
              {roles.map((role) => (
                <div
                  key={role.role}
                  className="flex items-center gap-4 py-3 px-4 rounded-lg hover:bg-gray-50"
                  style={{ marginLeft: `${(role.level - 1) * 40}px` }}
                >
                  <div className={`p-2 rounded-lg ${getRoleBadgeColor(role.role)}`}>
                    <Shield className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{role.display_name}</span>
                      <span className="text-xs text-gray-400">({role.role})</span>
                    </div>
                    <p className="text-sm text-gray-500">{role.description}</p>
                  </div>
                  <div className="text-right text-sm">
                    {role.reports_to && (
                      <div className="text-gray-500">
                        Reports to: <span className="font-medium">
                          {Array.isArray(role.reports_to) 
                            ? role.reports_to.map(r => getRoleDisplayName(r)).join(" & ")
                            : getRoleDisplayName(role.reports_to)
                          }
                        </span>
                      </div>
                    )}
                    {role.can_manage?.length > 0 && (
                      <div className="text-gray-500">
                        Manages: <span className="font-medium">
                          {role.can_manage.map(r => getRoleDisplayName(r)).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold mb-4">Role Descriptions</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {roles.map((role) => (
                <div key={role.role} className="flex items-start gap-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${getRoleBadgeColor(role.role)}`}>
                    {role.display_name}
                  </span>
                  <p className="text-sm text-gray-600">{role.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New Branch Dialog */}
      <Dialog open={showNewBranch} onOpenChange={setShowNewBranch}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Branch Name *</label>
                <Input
                  value={newBranch.name}
                  onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
                  placeholder="Mufulira Branch"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Branch Code *</label>
                <Input
                  value={newBranch.code}
                  onChange={(e) => setNewBranch({ ...newBranch, code: e.target.value.toUpperCase() })}
                  placeholder="MUF"
                  maxLength={5}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Address</label>
              <Input
                value={newBranch.address}
                onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">City *</label>
                <Input
                  value={newBranch.city}
                  onChange={(e) => setNewBranch({ ...newBranch, city: e.target.value })}
                  placeholder="Mufulira"
                />
              </div>
              <div>
                <label className="text-sm font-medium">State/Province</label>
                <Input
                  value={newBranch.state}
                  onChange={(e) => setNewBranch({ ...newBranch, state: e.target.value })}
                  placeholder="Copperbelt"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input
                  value={newBranch.phone}
                  onChange={(e) => setNewBranch({ ...newBranch, phone: e.target.value })}
                  placeholder="+260..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  value={newBranch.email}
                  onChange={(e) => setNewBranch({ ...newBranch, email: e.target.value })}
                  placeholder="branch@company.com"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_headquarters"
                checked={newBranch.is_headquarters}
                onChange={(e) => setNewBranch({ ...newBranch, is_headquarters: e.target.checked })}
              />
              <label htmlFor="is_headquarters" className="text-sm">This is the headquarters</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewBranch(false)}>Cancel</Button>
              <Button onClick={handleCreateBranch}>Create Branch</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Branch Dialog */}
      <Dialog open={showEditBranch} onOpenChange={setShowEditBranch}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Branch</DialogTitle>
          </DialogHeader>
          {selectedBranch && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Branch Name *</label>
                  <Input
                    value={selectedBranch.name}
                    onChange={(e) => setSelectedBranch({ ...selectedBranch, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Branch Code *</label>
                  <Input
                    value={selectedBranch.code}
                    onChange={(e) => setSelectedBranch({ ...selectedBranch, code: e.target.value.toUpperCase() })}
                    maxLength={5}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Address</label>
                <Input
                  value={selectedBranch.address || ""}
                  onChange={(e) => setSelectedBranch({ ...selectedBranch, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">City *</label>
                  <Input
                    value={selectedBranch.city}
                    onChange={(e) => setSelectedBranch({ ...selectedBranch, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">State/Province</label>
                  <Input
                    value={selectedBranch.state || ""}
                    onChange={(e) => setSelectedBranch({ ...selectedBranch, state: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    value={selectedBranch.phone || ""}
                    onChange={(e) => setSelectedBranch({ ...selectedBranch, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    value={selectedBranch.email || ""}
                    onChange={(e) => setSelectedBranch({ ...selectedBranch, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_headquarters"
                  checked={selectedBranch.is_headquarters}
                  onChange={(e) => setSelectedBranch({ ...selectedBranch, is_headquarters: e.target.checked })}
                />
                <label htmlFor="edit_is_headquarters" className="text-sm">This is the headquarters</label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEditBranch(false)}>Cancel</Button>
                <Button onClick={handleUpdateBranch}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={showAssignUser} onOpenChange={setShowAssignUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign User to Branch & Supervisor</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                    {selectedUser.name?.[0]}
                  </div>
                )}
                <div>
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-gray-500">{getRoleDisplayName(selectedUser.role)}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Assign to Branch</label>
                <select
                  className="w-full border rounded-md p-2 mt-1"
                  value={selectedUser.branch_id || ""}
                  onChange={(e) => handleAssignUserToBranch(selectedUser.user_id, e.target.value || null)}
                >
                  <option value="">Unassigned</option>
                  {branches.map((branch) => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Reports To (Supervisor)</label>
                <select
                  className="w-full border rounded-md p-2 mt-1"
                  value={selectedUser.reports_to || ""}
                  onChange={(e) => handleAssignSupervisor(selectedUser.user_id, e.target.value || null)}
                >
                  <option value="">None</option>
                  {users
                    .filter(u => u.user_id !== selectedUser.user_id)
                    .map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name} ({getRoleDisplayName(u.role)})
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowAssignUser(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchManagement;
